import React, { useMemo } from "react";
import { NO_SEARCH_RESULTS, SUCCESS_MESSAGES, REPORTED_USER_TABLE } from "@/utils/constants/appConstants/commonConstants";
import { useFeaturePermission } from "@/utils/hooks/useFeaturePermission";
import { Link, useNavigate } from "react-router-dom";
import { DateFormatter, TimeFormatter, convertToSentenceCase } from "@/utils/functions/helperFunctions";
import { ReportedUsersTableSkeletonDesktop, ReportedUsersTableSkeletonMobile } from "@/components/skeletons";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import NoDataAvailable from "@/components/cards/NoDataAvailable";
import { MobileListChevron } from "@/components/cards/MobileListChevron";
import NoFilterDataCard from "@/components/cards/NoFilterDataCard";
import { Button, Dropdown } from 'react-bootstrap';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import SuspendUserModal from "@/components/modals/SuspendUserModal";
import ContactModal from "@/components/modals/ContactModal";
import { errorToasterMessage } from "@/utils/functions/toaster";
import { successToasterMessage } from "@/utils/functions/toaster";
import { suspendUser, markAsSafe, contactUser } from "@/services/user.service";
import { FEATURE_PERMISSIONS } from "@/utils/constants/featurePermissions";
interface ReportedUserTableProps {
    allFetched: boolean;
    users: any[];
    setUsers: (users: any) => void;
    search: (searchText: string) => void;
    reset: () => void;
    isLoading: boolean;
    dataLength: number;
    sorting: any;
    setSorting: ({ }) => void;
    searchItem: string | null;
    title: string;
    setPageNo: (pageNo: number) => void;
    dataload: boolean;
    resetDataQueries: () => void;
    id: string;
    onSuspendUser: (userId: string) => void;
}

const ReportedUsersTable: React.FC<ReportedUserTableProps> = (props) => {
    const [emailAddressStatus, setEmailAddressStatus] = React.useState(true);
    const [reportedAtStatus, setReportedAtStatus] = React.useState(true);
    const [sortElement, setSortElement] = React.useState("reported_at");
    const [showSuspendModal, setShowSuspendModal] = React.useState(false);
    const [showContactModal, setShowContactModal] = React.useState(false);
    const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);
    const [statusLoading, setStatusLoading] = React.useState(false);
    const [contactLoading, setContactLoading] = React.useState(false);
    const isAdminPortalSuspentionPermitted = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalSuspendUser);
    const isAdminPortalUpdateReportedUserPermitted = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalUpdateReportedUser);
    const isCompactLayout = useCompactListLayout();
    const navigate = useNavigate();

    const emailAddressSort = () => {
        setSortElement("email_address");
        props.setPageNo(1);
        props.setSorting({ email_address: !emailAddressStatus ? 1 : -1 });
        setEmailAddressStatus(!emailAddressStatus);
    };

    const reportedAtSort = () => {
        setReportedAtStatus(!reportedAtStatus);
        setSortElement("reported_at");
        props.setPageNo(1);
        props.setSorting({ reported_at: !reportedAtStatus ? 1 : -1 });
    };

    const handleSuspendClick = (userId: string) => {
        setSelectedUserId(userId);
        setShowSuspendModal(true);
    };

    const handleMarkAsSafe = (userId: string) => {
        setStatusLoading(true);
        markAsSafe(
            userId,
            (response: any) => {
                successToasterMessage(SUCCESS_MESSAGES.USER_MARKED_SAFE);
                setStatusLoading(false);
            },
            (err: any) => {
                errorToasterMessage(err.response.data.message);
                setStatusLoading(false);
            }
        );
    };

    const suspendUserhandler = (reason: string) => {
        if (!selectedUserId) return;
        setStatusLoading(true);
        suspendUser(
            { user_id: selectedUserId, reason: reason },
            (response: any) => {
                successToasterMessage(SUCCESS_MESSAGES.USER_SUSPENDED);
                setStatusLoading(false);
                setShowSuspendModal(false);
                props.onSuspendUser(selectedUserId);
            },
            (err: any) => {
                errorToasterMessage(err.response.data.message);
                setStatusLoading(false);
                setShowSuspendModal(false);
            }
        );
    };

    const handleContactUser = (userId: string) => {
        setSelectedUserId(userId);
        setShowContactModal(true);
    };

    const handleContactSubmit = async (subject: string, body: string) => {
        if (!selectedUserId) return;
        setContactLoading(true);
        try {
            await contactUser(
                selectedUserId,
                { subject: subject, message: body },
                (response: any) => {
                    successToasterMessage(REPORTED_USER_TABLE.MESSAGE_SENT_SUCCESS);
                    setShowContactModal(false);
                    setContactLoading(false);
                },
                (err: any) => {
                    errorToasterMessage(err.response.data.message);
                    setContactLoading(false);
                }
            );
        } catch (error) {
            setContactLoading(false);
        }
    };

    return isCompactLayout ? (
        props.users && !props.dataload ? (
            props.users?.length > 0 ? (
                <div className="envelope_table">
                    {props.users?.map((user: any, key: number) => (
                            <div className="envelope_tr gs-mobile-list-card" key={`${user.id}-${key}`} onClick={() => {
                                navigate(`/reported-user/${user.id}`, {
                                    state: {
                                        userName: convertToSentenceCase(`${user.first_name ? user.first_name : ""} ${user.last_name ? user.last_name : ""}`),
                                        email: user.email_address
                                    }
                                });
                            }}>
                                <div className="gs-mobile-card-icon">
                                    <em id={`${props.id}-icon-${key}-mb`} className="fw-400 icon ni ni-user"></em>
                                </div>
                                <div className="env_meta_data gs-mobile-list-card__content w-100">
                                    <div className="user-info">
                                        <p className="tb-lead m-0" id={`${props.id}-email-${key}-mb`}>{user.email_address}</p>
                                        <p className="m-0 text-soft" id={`${props.id}-user-name-${key}-mb`}>
                                            {convertToSentenceCase(`${user.first_name ? user.first_name : ""} ${user.last_name ? user.last_name : ""}`)}
                                        </p>
                                    </div>
                                    <div className="text-dark">
                                        <span className="fw-bold">{REPORTED_USER_TABLE.NO_OF_REPORT_ENVELOPES}: </span>
                                        <span className="tb-lead-sub" id={`${props.id}-reason-${key}-mb`}>{user.history?.filter((history: any) => history.type === "reported").length}</span>
                                    </div>
                                    <div className="text-dark">
                                        <span className="fw-bold">{REPORTED_USER_TABLE.STATUS} </span>
                                        <span className={`badge badge-dim badge-sm pt-1 w-50 justify-content-center pb-1 text-capitalize ${user.status === 'suspended' ? 'badge-danger' : user.status === 'active' ? 'badge-success' : 'badge-light'}`}>
                                            {user.status}
                                        </span>
                                    </div>
                                    <div className="d-flex justify-content-between pt-1">
                                        <div className="dropdown">
                                            <button className="btn btn-sm btn-outline-primary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                                                {REPORTED_USER_TABLE.ACTIONS}
                                            </button>
                                            <ul className="dropdown-menu">
                                                {isAdminPortalSuspentionPermitted && <li>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => handleSuspendClick(user.id)}
                                                        disabled={user.status === 'suspended'}
                                                    >
                                                        {REPORTED_USER_TABLE.SUSPEND_USER}
                                                    </button>
                                                </li>}
                                                <li>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => handleContactUser(user.id)}
                                                    >
                                                        {REPORTED_USER_TABLE.CONTACT_USER}
                                                    </button>
                                                </li>
                                                {isAdminPortalUpdateReportedUserPermitted && <li>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => handleMarkAsSafe(user.id)}
                                                        disabled={user.status === 'active'}
                                                    >
                                                        {REPORTED_USER_TABLE.MARK_AS_SAFE}
                                                    </button>
                                                </li>}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <MobileListChevron
                                    id={`${props.id}-redirect-${key}-mb`}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        navigate(`/reported-user/${user.id}`, {
                                            state: {
                                                userName: convertToSentenceCase(`${user.first_name ? user.first_name : ""} ${user.last_name ? user.last_name : ""}`),
                                                email: user.email_address
                                            }
                                        });
                                    }}
                                />
                            </div>
                    ))}
                </div>
            ) : props.searchItem ? (
                <NoDataAvailable><p id={`${props.id}-no-search-result`}>{REPORTED_USER_TABLE.NO_RESULTS}</p></NoDataAvailable>
            ) : (
                <NoDataAvailable>
                    <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                </NoDataAvailable>
            )
        ) : (
            <ReportedUsersTableSkeletonMobile />
        )
    ) : (
        <>
            <table className="nk-tb-list nk-tb-ulist table-weight gs-reported-users-table" style={{ tableLayout: "fixed", width: "100%" }}>
                <colgroup>
                    <col className="gs-reported-users-col-user" />
                    <col className="gs-reported-users-col-count" />
                    <col className="gs-reported-users-col-status" />
                    <col className="gs-reported-users-col-actions" />
                </colgroup>
                <thead>
                    <tr className="nk-tb-item nk-tb-head">
                        <th className="nk-tb-col gs-reported-users-col-user cursor-pointer" onClick={emailAddressSort}>
                            <span id={`${props.id}-user-email-th`}>{REPORTED_USER_TABLE.USER}</span>
                            <em id={`${props.id}-user-email-th-sort-icon`} className={`ml-1 icon ni ${sortElement === "email_address" ? (emailAddressStatus === true ? "ni-upword-alt-fill" : "ni-downward-alt-fill") : ""}`}></em>
                        </th>
                        <th className="nk-tb-col gs-reported-users-col-count">
                            <span id={`${props.id}-reason-th`}>{REPORTED_USER_TABLE.NO_OF_REPORT_ENVELOPES}</span>
                        </th>
                        <th className="nk-tb-col gs-reported-users-col-status">
                            <span id={`${props.id}-status-th`}>{REPORTED_USER_TABLE.STATUS}</span>
                        </th>
                        <th className="nk-tb-col gs-reported-users-col-actions">
                            <span id={`${props.id}-actions-th`}>{REPORTED_USER_TABLE.ACTIONS}</span>
                        </th>
                    </tr>
                </thead>
                {props.users && !props.dataload ? (
                    props.users?.length > 0 ? (
                        <tbody>
                            {props.users.map((user: any, key: number) => (
                                    <tr className="nk-tb-item" key={`${user.id}-${key}`} >
                                        <td className="nk-tb-col gs-reported-users-col-user cursor-pointer" onClick={() => {
                                            navigate(`/reported-user/${user.id}`, {
                                                state: {
                                                        userName: convertToSentenceCase(`${user.first_name ? user.first_name : ""} ${user.last_name ? user.last_name : ""}`),
                                                        email: user.email_address
                                                    }
                                                });
                                        }}>
                                            <div className="d-flex align-items-center">
                                                <div className="icon mr-3 fs-5">
                                                    <em id={`${props.id}-icon-${key}`} className="fw-400 icon ni ni-user"></em>
                                                </div>
                                            <div className="user-info">
                                                <span className="tb-lead" id={`${props.id}-email-${key}`}>{user.email_address}</span>
                                                <span id={`${props.id}-user-name-${key}`}>
                                                    {convertToSentenceCase(`${user.first_name ? user.first_name : ""} ${user.last_name ? user.last_name : ""}`)}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="nk-tb-col gs-reported-users-col-count">
                                            <span className="tb-amount" id={`${props.id}-reason-${key}`}>{user.history?.filter((history: any) => history.type === "reported").length}</span>
                                        </td>
                                        <td className="nk-tb-col gs-reported-users-col-status">
                                            <span className={`badge badge-dim badge-sm pt-1 w-50 justify-content-center pb-1 text-capitalize ${user.status === 'suspended' ? 'badge-danger' : user.status === 'active' ? 'badge-success' : 'badge-light'}`}>
                                                {user.status}
                                            </span>
                                        </td>
                                        <td className="nk-tb-col gs-reported-users-col-actions">
                                            <Dropdown size="sm" as={ButtonGroup} drop="down-centered" align="end">
                                                <button className="btn btn-primary px-1" id={`-table-toggle`}>{REPORTED_USER_TABLE.ACTIONS}</button>
                                                <Dropdown.Toggle variant='dark' className='btn btn-primary px-1' id={`-table-toggle`}>&#x25bc;</Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    {isAdminPortalSuspentionPermitted && <Dropdown.Item
                                                        onClick={() => handleSuspendClick(user.id)}
                                                        disabled={user.status === 'suspended'}
                                                        className="d-flex align-items-center"
                                                    >
                                                        <em className="icon ni ni-na pr-2"></em>
                                                        {REPORTED_USER_TABLE.SUSPEND_USER}
                                                    </Dropdown.Item>}
                                                    <Dropdown.Item
                                                        onClick={() => handleContactUser(user.id)}
                                                        className="d-flex align-items-center"
                                                    >
                                                        <em className="icon ni ni-mail pr-2"></em>
                                                        {REPORTED_USER_TABLE.CONTACT_USER}
                                                    </Dropdown.Item>
                                                    {isAdminPortalUpdateReportedUserPermitted && <Dropdown.Item
                                                        onClick={() => handleMarkAsSafe(user.id)}
                                                        className="d-flex align-items-center"
                                                    >
                                                        <em className="icon ni ni-flag pr-2"></em>
                                                        {REPORTED_USER_TABLE.MARK_AS_SAFE}
                                                    </Dropdown.Item>}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </td>
                                    </tr>
                            ))}
                        </tbody>
                    ) : props.searchItem ? (
                        <tbody>
                            <tr>
                                <td colSpan={4} className="text-center">
                                    <NoDataAvailable><p id={`${props.id}-no-search-result`}>{REPORTED_USER_TABLE.NO_RESULTS}</p></NoDataAvailable>
                                </td>
                            </tr>
                        </tbody>
                    ) : (
                        <tbody>
                            <tr>
                                <td colSpan={4} className="text-center">
                                    <NoDataAvailable>
                                        <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                                    </NoDataAvailable>
                                </td>
                            </tr>
                        </tbody>
                    )
                ) : (
                    <tbody>
                        <ReportedUsersTableSkeletonDesktop />
                    </tbody>
                )}
            </table>
            <SuspendUserModal show={showSuspendModal} onHide={() => setShowSuspendModal(false)} onSuspend={suspendUserhandler} loading={statusLoading} />
            <ContactModal
                show={showContactModal}
                onHide={() => setShowContactModal(false)}
                onContact={handleContactSubmit}
                loading={contactLoading}
            />
        </>
    );
};

export default ReportedUsersTable; 